import { useCallback, useRef, type RefObject } from "react";
import { IntelligenceEngineApi } from "@src/services/extractionApi";
import { buildExtractedResultViewModel } from "@utils/index";
import {
  ProcessStatus,
  ProcessItemStatus,
  type IProcessInfoViewModel,
  type IProcessItemViewModel,
  type IExtractedResultViewModel,
  type IExtractedNodeViewModel,
  type IProcessedResultsFilterViewModel,
  SupportedDownloadFileType,
  ApiError,
} from "@models/index";

/** Outcome of opening one document from a review link. */
export type ReviewItemState =
  | { kind: "ready"; processInfo: IProcessInfoViewModel; item: IProcessItemViewModel }
  | { kind: "notFound" }
  | { kind: "wrongDoctype"; doctypeCode: string }
  | { kind: "notReady"; status: string }
  | { kind: "error"; message: string };

export const useResultsManagement = (
  intelligenceEngineApi: IntelligenceEngineApi,
  setIsLoadingResults: (value: boolean) => void,
  setIsLoadingResult: (value: boolean) => void,
  setError: (error: string | null) => void,
  setProcessResultsInfo: React.Dispatch<
    React.SetStateAction<IProcessInfoViewModel[]>
  >,
  setExtractedResult: (result: IExtractedResultViewModel | undefined) => void,
  setSelectedProcessResultInfo: (
    info: IProcessInfoViewModel | undefined,
  ) => void,
  setSelectedProcessItem: (item: IProcessItemViewModel | undefined) => void,
  setTotalPages: (pages: number) => void,
  setCurrentPage: (page: number) => void,
  setSelectedNode: (node: IExtractedNodeViewModel | undefined) => void,
  connectToProcessNotifications: (processId: string) => void,
  showError: (message: string) => void,
  uploadingProcessIdsRef: RefObject<Set<string>>,
) => {
  const isLoadingResultsRef = useRef(false);
  const activeDoctypeRef = useRef<string>("");

  const getProcessedResultsInfo = useCallback(
    async (doctypeCode: string, filter?: IProcessedResultsFilterViewModel) => {
      // Allow new calls when the doctype changes, only guard duplicate calls for the same doctype
      if (
        isLoadingResultsRef.current &&
        activeDoctypeRef.current === doctypeCode
      )
        return;
      activeDoctypeRef.current = doctypeCode;

      const filterToUse = filter || {
        startDate: null,
        endDate: null,
        fileName: null,
        userId: null,
        processId: null,
      };

      isLoadingResultsRef.current = true;
      setIsLoadingResults(true);
      setError(null);

      try {
        const processes = await intelligenceEngineApi.searchExtractionProcesses(
          doctypeCode,
          filterToUse,
          filterToUse.limit ?? undefined,
        );

        // Drop processes whose file upload is still in flight locally —
        // backend has the PENDING record from the presigned-URL POST, but
        // useFileUpload owns the row insertion until the S3 upload completes.
        const visibleItems = processes.filter(
          (item) => !uploadingProcessIdsRef.current.has(item.id),
        );

        setProcessResultsInfo(visibleItems);

        visibleItems.forEach((item) => {
          if (
            item.status === ProcessStatus.Pending ||
            item.status === ProcessStatus.Processing
          ) {
            connectToProcessNotifications(item.id);
          }
        });
      } catch (err) {
        const errorMessage =
          err instanceof ApiError
            ? err.getUserMessage()
            : err instanceof Error
              ? err.message
              : "Failed to fetch processed results";
        setError(errorMessage);
        showError(errorMessage);
      } finally {
        setIsLoadingResults(false);
        isLoadingResultsRef.current = false;
      }
    },
    [
      intelligenceEngineApi,
      setIsLoadingResults,
      setError,
      setProcessResultsInfo,
      connectToProcessNotifications,
      showError,
      uploadingProcessIdsRef,
    ],
  );

  const getExtractedResult = useCallback(
    async (
      processInfo: IProcessInfoViewModel,
      item?: IProcessItemViewModel,
    ) => {
      const targetItem = item || processInfo.items[0];
      if (!targetItem?.id) {
        setError("Invalid process result");
        return;
      }

      setSelectedProcessResultInfo(processInfo);
      setSelectedProcessItem(targetItem);
      setIsLoadingResult(true);
      setError(null);

      try {
        const extracted = await intelligenceEngineApi.getExtractedProcess(
          processInfo.id,
          targetItem.id,
        );
        const viewModel = buildExtractedResultViewModel(extracted);

        setTotalPages(viewModel.noPages);
        setExtractedResult(viewModel);

        if (viewModel.noPages > 0) {
          setCurrentPage(1);
          setSelectedNode(undefined);
        }
      } catch (err) {
        const errorMessage =
          err instanceof ApiError
            ? err.getUserMessage()
            : err instanceof Error
              ? err.message
              : "Extraction failed";
        setError(errorMessage);
        showError(errorMessage);
      } finally {
        setIsLoadingResult(false);
      }
    },
    [
      intelligenceEngineApi,
      setIsLoadingResult,
      setError,
      setSelectedProcessResultInfo,
      setSelectedProcessItem,
      setExtractedResult,
      setTotalPages,
      setCurrentPage,
      setSelectedNode,
      showError,
    ],
  );

  /**
   * Open one document by id, for the review link. The backend already scopes
   * processes to the caller's organisation (403/404 for anyone else); this also
   * refuses a process of another doctype than the page's and an item that has
   * not finished, then loads the result through the normal path.
   */
  const openProcessItem = useCallback(
    async (
      processId: string,
      itemId: string,
      doctypeCode: string,
    ): Promise<ReviewItemState> => {
      let processInfo: IProcessInfoViewModel;
      try {
        processInfo =
          await intelligenceEngineApi.getExtractionProcessesInfo(processId);
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.statusCode === 403 || err.statusCode === 404)
        ) {
          return { kind: "notFound" };
        }
        return {
          kind: "error",
          message:
            err instanceof ApiError
              ? err.getUserMessage()
              : err instanceof Error
                ? err.message
                : "Failed to load the document",
        };
      }

      if (processInfo.doctypeCode && processInfo.doctypeCode !== doctypeCode) {
        return { kind: "wrongDoctype", doctypeCode: processInfo.doctypeCode };
      }
      const item = processInfo.items.find((i) => i.id === itemId);
      if (!item) return { kind: "notFound" };
      if (
        item.status !== ProcessItemStatus.Completed &&
        item.status !== ProcessItemStatus.Failed
      ) {
        return { kind: "notReady", status: item.status };
      }

      await getExtractedResult(processInfo, item);
      return { kind: "ready", processInfo, item };
    },
    [intelligenceEngineApi, getExtractedResult],
  );

  const downloadResult = useCallback(
    async (
      processId: string,
      itemId: string,
      fileType: SupportedDownloadFileType = SupportedDownloadFileType.Excel,
    ) => {
      try {
        setError(null);
        const downloaded = await intelligenceEngineApi.downloadResult(
          processId,
          itemId,
          fileType,
        );
        intelligenceEngineApi.downloadFile(downloaded);
      } catch (err) {
        const errorMessage =
          err instanceof ApiError
            ? err.getUserMessage()
            : err instanceof Error
              ? err.message
              : "Failed to download result";
        setError(errorMessage);
        showError(errorMessage);
      }
    },
    [intelligenceEngineApi, setError, showError],
  );

  const downloadBatchResults = useCallback(
    async (processId: string): Promise<void> => {
      try {
        setError(null);
        const downloaded =
          await intelligenceEngineApi.downloadBatchResults(processId);
        intelligenceEngineApi.downloadFile(downloaded);
      } catch (err) {
        const errorMessage =
          err instanceof ApiError
            ? err.getUserMessage()
            : err instanceof Error
              ? err.message
              : "Failed to download batch results";
        setError(errorMessage);
        showError(errorMessage);
      }
    },
    [intelligenceEngineApi, setError, showError],
  );

  const deleteProcess = useCallback(
    async (processId: string): Promise<boolean> => {
      try {
        setError(null);
        await intelligenceEngineApi.deleteProcess(processId);
        // Optimistic list pruning so the row vanishes immediately.
        setProcessResultsInfo((prev) =>
          prev.filter((p) => p.id !== processId),
        );
        setSelectedProcessResultInfo(undefined);
        setSelectedProcessItem(undefined);
        setExtractedResult(undefined);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof ApiError
            ? err.getUserMessage()
            : err instanceof Error
              ? err.message
              : "Failed to delete process";
        setError(errorMessage);
        showError(errorMessage);
        return false;
      }
    },
    [
      intelligenceEngineApi,
      setError,
      setExtractedResult,
      setProcessResultsInfo,
      setSelectedProcessItem,
      setSelectedProcessResultInfo,
      showError,
    ],
  );

  const deleteProcessItem = useCallback(
    async (processId: string, itemId: string): Promise<boolean> => {
      try {
        setError(null);
        await intelligenceEngineApi.deleteProcessItem(processId, itemId);
        // Drop the item from its parent process in-place; if it was the last
        // one we also drop the parent so the UI stays consistent.
        setProcessResultsInfo((prev) =>
          prev.flatMap((p) => {
            if (p.id !== processId) return [p];
            const remaining = p.items.filter((item) => item.id !== itemId);
            if (remaining.length === 0) return [];
            return [{ ...p, items: remaining }];
          }),
        );
        setSelectedProcessItem(undefined);
        setExtractedResult(undefined);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof ApiError
            ? err.getUserMessage()
            : err instanceof Error
              ? err.message
              : "Failed to delete process item";
        setError(errorMessage);
        showError(errorMessage);
        return false;
      }
    },
    [
      intelligenceEngineApi,
      setError,
      setExtractedResult,
      setProcessResultsInfo,
      setSelectedProcessItem,
      showError,
    ],
  );

  return {
    getProcessedResultsInfo,
    getExtractedResult,
    openProcessItem,
    downloadResult,
    downloadBatchResults,
    deleteProcess,
    deleteProcessItem,
  };
};
